import axios from 'axios';
import {
  estimateInteractiveInputs,
  extractInputPrompts,
  formatRunOutput,
  friendlyStdinError,
  getStudentAppApiBaseUrl,
  getStudentAuthHeaders,
  longestCommonPrefix,
  prepareStdin,
} from '../../utils/practiceIdeUtils';

/**
 * Run code via the playground API (primary) with optional content-scoped fallback.
 */
export async function runPracticeCode({
  language,
  code,
  stdin,
  studentPhone,
  fallbackContentId,
}) {
  const apiBaseUrl = getStudentAppApiBaseUrl();
  const payload = { studentPhone, language, code, stdin: prepareStdin(stdin) };
  const headers = getStudentAuthHeaders();

  const urls = [
    `${apiBaseUrl}/lms/playground/run`,
    ...(fallbackContentId ? [`${apiBaseUrl}/lms/coding/${fallbackContentId}/run`] : []),
  ];

  let lastError = null;

  for (const url of urls) {
    try {
      const response = await axios.post(url, payload, { headers });
      return response.data?.data || {};
    } catch (err) {
      lastError = err;
    }
  }

  throw (
    lastError?.response?.data?.message ||
    lastError?.response?.data?.error ||
    lastError?.message ||
    'Run failed.'
  );
}

export async function executePracticeRun({
  language,
  code,
  stdin,
  studentPhone,
  fallbackContentId,
  skipStdinCheck = false,
}) {
  const inputCount = estimateInteractiveInputs(code, language);
  const preparedStdin = prepareStdin(stdin);

  if (!skipStdinCheck && inputCount > 0 && !preparedStdin.trim()) {
    throw new Error(
      `Program needs ${inputCount} input value(s). Type them in the terminal (one per line), then click Run.`,
    );
  }

  const result = await runPracticeCode({
    language,
    code,
    stdin: preparedStdin,
    studentPhone,
    fallbackContentId,
  });

  const stdinHelp = friendlyStdinError(result.stderr || result.error || '', inputCount);
  if (stdinHelp) {
    return { output: result.stdout || '', error: stdinHelp };
  }

  return formatRunOutput(result);
}

async function probePromptAtStep({
  language,
  code,
  studentPhone,
  fallbackContentId,
  priorInputs,
  stepIndex,
  totalInputs,
  priorStdoutLength,
}) {
  const buildStdin = (probeValue) => {
    const lines = [...priorInputs];
    while (lines.length < stepIndex) {
      lines.push('1');
    }
    lines[stepIndex] = String(probeValue);
    while (lines.length < totalInputs) {
      lines.push('1');
    }
    return prepareStdin(lines.join('\n'));
  };

  const [probeA, probeB] = await Promise.all([
    runPracticeCode({
      language,
      code,
      stdin: buildStdin(1),
      studentPhone,
      fallbackContentId,
    }),
    runPracticeCode({
      language,
      code,
      stdin: buildStdin(-1),
      studentPhone,
      fallbackContentId,
    }),
  ]);

  const sharedPrefix = longestCommonPrefix(probeA.stdout || '', probeB.stdout || '');
  return sharedPrefix.slice(priorStdoutLength);
}

/** Resolve prompts for each interactive input (parse source, then probe if needed). */
export async function resolveInteractivePrompts({
  language,
  code,
  studentPhone,
  fallbackContentId,
}) {
  const inputCount = estimateInteractiveInputs(code, language);
  if (inputCount <= 0) {
    return [];
  }

  const extracted = extractInputPrompts(code, language);
  const prompts = [];
  let priorStdoutLength = 0;
  const probePriorInputs = [];

  for (let step = 0; step < inputCount; step += 1) {
    let prompt = extracted[step] || '';
    if (!prompt) {
      prompt = await probePromptAtStep({
        language,
        code,
        studentPhone,
        fallbackContentId,
        priorInputs: probePriorInputs,
        stepIndex: step,
        totalInputs: inputCount,
        priorStdoutLength,
      });
    }
    prompts.push(prompt);
    priorStdoutLength += prompt.length;
    probePriorInputs.push('1');
  }

  return prompts;
}
